import React, { useEffect, useState } from 'react';
import { Plus, Calendar, DollarSign, Award, Users, Settings, QrCode, Hash, ShoppingCart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { Campaign, Organization } from '../../lib/supabase';
import CreateRaffleForm from './CreateRaffleForm';
import PrizesModal from './PrizesModal';
import SettingsModal from './SettingsModal';
import QRCodeModal from './QRCodeModal';

const CampaignManager = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<{
    id: string;
    modal: 'prizes' | 'settings' | 'qrcode';
    data: Campaign;
  } | null>(null);

  useEffect(() => {
    fetchOrganizations();
    fetchCampaigns();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrganizations(data || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCampaigns(data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: Campaign['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleModalClose = () => {
    setSelectedCampaign(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white">Raffle Manager</h1>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center px-4 py-2 bg-white text-purple-600 rounded-md hover:bg-white/90 transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Create a Raffle
        </button>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl p-8 text-center">
          <p>Loading raffles...</p>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 text-center border border-white/20">
          <h3 className="text-xl font-semibold text-white mb-4">No Raffles Yet</h3>
          <p className="text-white/80 mb-6">Create your first raffle to start managing your events.</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center px-6 py-3 bg-white text-purple-600 rounded-md hover:bg-white/90 transition-colors mx-auto"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create a Raffle
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map((campaign) => (
            <div key={campaign.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(campaign.status)}`}>
                    {campaign.status}
                  </span>
                </div>

                <p className="text-gray-600 text-sm mb-4">
                  {campaign.description || 'No description provided'}
                </p>

                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>
                      {new Date(campaign.start_date).toLocaleDateString()} - {new Date(campaign.end_date).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <DollarSign className="h-4 w-4 mr-2" />
                    <span>Ticket Price: {campaign.ticket_price.toFixed(2)}</span>
                  </div>
                  {campaign.max_tickets && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="h-4 w-4 mr-2" />
                      <span>Max Tickets: {campaign.max_tickets}</span>
                    </div>
                  )}
                  <div className="flex items-center text-xs text-gray-400">
                    <Hash className="h-3 w-3 mr-1" />
                    <span>{campaign.id}</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 px-6 py-4">
                <div className="flex justify-between">
                  <button
                    onClick={() => setSelectedCampaign({ id: campaign.id, modal: 'prizes', data: campaign })}
                    className="flex items-center text-sm text-purple-600 hover:text-purple-800"
                  >
                    <Award className="h-4 w-4 mr-1" />
                    Prizes
                  </button>
                  <button
                    onClick={() => setSelectedCampaign({ id: campaign.id, modal: 'qrcode', data: campaign })}
                    className="flex items-center text-sm text-purple-600 hover:text-purple-800"
                  >
                    <QrCode className="h-4 w-4 mr-1" />
                    QR Code
                  </button>
                  <button
                    onClick={() => setSelectedCampaign({ id: campaign.id, modal: 'settings', data: campaign })}
                    className="flex items-center text-sm text-gray-600 hover:text-gray-800"
                  >
                    <Settings className="h-4 w-4 mr-1" />
                    Settings
                  </button>
                </div>
              </div>

              {campaign.status === 'active' && (
                <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                  <Link
                    to={`/checkout/${campaign.id}`}
                    className="flex items-center justify-center w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Buy Tickets
                  </Link>
                </div>
              )}

              {campaign.status === 'completed' && (
                <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                  <Link
                    to={`/admin/proceeds/${campaign.id}`}
                    className="flex items-center justify-center w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                  >
                    <DollarSign className="h-4 w-4 mr-2" />
                    Manage Proceeds
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showCreateForm && (
        <CreateRaffleForm
          onClose={() => setShowCreateForm(false)}
          onSuccess={fetchCampaigns}
        />
      )}

      {selectedCampaign?.modal === 'prizes' && (
        <PrizesModal
          campaignId={selectedCampaign.id}
          onClose={handleModalClose}
        />
      )}

      {selectedCampaign?.modal === 'settings' && (
        <SettingsModal
          campaign={selectedCampaign.data}
          onClose={handleModalClose}
          onUpdate={fetchCampaigns}
        />
      )}

      {selectedCampaign?.modal === 'qrcode' && (
        <QRCodeModal
          campaign={selectedCampaign.data}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default CampaignManager;